import { useEffect } from 'react';

/**
 * The help centre in a white-label reseller's name.
 *
 * The same docs are served on a reseller's own `docs.<root>` (its domain setup,
 * infra/partner-cert). There, nothing may say « Noé »: this component, mounted
 * once in the header, asks the reseller's API (`api.<root>`) for its public
 * branding and rewrites the page in the browser — and keeps doing it for what
 * appears later (search results, menus, dialogs):
 *
 *  - « Noé » → the reseller's name, Noé's hosts (book./app./docs.wearenoe.fr)
 *    → the reseller's, in text and links;
 *  - the logo and the tab icon → the reseller's;
 *  - what only Noé has (the developers' API section, its site and legal pages)
 *    is hidden.
 *
 * Done in the browser on purpose: the search index is compressed, so no proxy
 * can rewrite it. The reseller's vhost hides the page until `html.wl-ready`
 * (2.5 s at most), so « Noé » never flashes. On Noé's own hosts it does nothing.
 */

type Branding = { enabled: true; displayName: string; logoUrl: string | null; logoDarkUrl: string | null; faviconUrl: string | null; websiteUrl?: string | null };

const NOE_HOST = /(^|\.)wearenoe\.fr$|^localhost$|^127\.0\.0\.1$/;
const READY = 'wl-ready';

function rootOf(hostname: string): string | null {
  const m = /^(?:dev-)?docs\.(.+)$/.exec(hostname);
  return m ? m[1] : null;
}

export function WhiteLabel() {
  useEffect(() => {
    const host = window.location.hostname.toLowerCase();
    const reveal = () => document.documentElement.classList.add(READY);
    // Already rebranded by brand-server/server.mjs, before it left the box.
    if (document.documentElement.dataset.brandServed) return reveal();
    if (NOE_HOST.test(host)) return reveal();
    const root = rootOf(host);
    if (!root) return reveal();

    let observer: MutationObserver | null = null;
    let cancelled = false;
    fetch(`https://api.${root}/v1/public/partner-branding?hostname=${encodeURIComponent(host)}`, { credentials: 'omit' })
      .then((r) => (r.ok ? r.json() : null))
      .then((b: Branding | { enabled: false } | null) => {
        if (cancelled || !b || !b.enabled) return;
        const apply = rewriter(b, root);
        // Noé-only section: never reachable here.
        if (window.location.pathname.startsWith('/developpeurs')) window.location.replace('/');
        apply(document.body);
        document.title = textOf(document.title, b, root);
        observer = new MutationObserver((mutations) => {
          for (const m of mutations) {
            if (m.type === 'characterData' && m.target.parentElement) apply(m.target.parentElement);
            m.addedNodes.forEach((n) => (n.nodeType === Node.TEXT_NODE ? n.parentElement && apply(n.parentElement) : n instanceof Element && apply(n)));
          }
          if (/Noé|wearenoe/.test(document.title)) document.title = textOf(document.title, b, root);
        });
        observer.observe(document.body, { childList: true, subtree: true, characterData: true });
      })
      .catch(() => {
        /* the page shows as it is */
      })
      .finally(reveal);
    return () => {
      cancelled = true;
      observer?.disconnect();
    };
  }, []);
  return null;
}

/** A text in the reseller's name: its brand, its hosts. */
function textOf(s: string, b: Branding, root: string): string {
  return s
    .replace(/NOÉ/g, b.displayName.toUpperCase())
    .replace(/Noé/g, b.displayName)
    .replace(/\b(book|app|docs)\.wearenoe\.fr/g, `$1.${root}`)
    .replace(/\bwearenoe\.fr\b/g, root);
}

function rewriter(b: Branding, root: string) {
  const hrefOf = (href: string): string | null => {
    let u: URL;
    try {
      u = new URL(href, window.location.href);
    } catch {
      return href;
    }
    if (!/(^|\.)wearenoe\.fr$/.test(u.hostname)) return href;
    const sub = /^(book|app|docs|dev-app|dev-docs)\./.exec(u.hostname)?.[1];
    if (sub === 'docs' || sub === 'dev-docs') return `${window.location.origin}${u.pathname}${u.search}${u.hash}`;
    if (sub) return `https://${sub.replace('dev-', '')}.${root}${u.pathname}${u.search}${u.hash}`;
    // Noé's own site and legal pages: the reseller's site when it has one, else nothing.
    return u.pathname === '/' && b.websiteUrl ? b.websiteUrl : null;
  };

  return (el: Element) => {
    // Text, wherever it says Noé (scripts and styles aside).
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, {
      acceptNode: (n) => (n.parentElement?.closest('script,style') ? NodeFilter.FILTER_REJECT : /Noé|NOÉ|wearenoe/.test(n.nodeValue ?? '') ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP),
    });
    const texts: Text[] = [];
    while (walker.nextNode()) texts.push(walker.currentNode as Text);
    for (const t of texts) t.nodeValue = textOf(t.nodeValue ?? '', b, root);

    const all = [el, ...Array.from(el.querySelectorAll('*'))];
    for (const node of all) {
      for (const attr of ['title', 'alt', 'aria-label', 'placeholder']) {
        const v = node.getAttribute(attr);
        if (v && /Noé|wearenoe/.test(v)) node.setAttribute(attr, textOf(v, b, root));
      }
      if (node instanceof HTMLAnchorElement && node.getAttribute('href')) {
        const raw = node.getAttribute('href')!;
        if (raw.startsWith('/developpeurs')) {
          (node.closest('li') ?? node).setAttribute('hidden', '');
          continue;
        }
        if (/wearenoe\.fr/.test(raw)) {
          const next = hrefOf(raw);
          if (next) node.setAttribute('href', next);
          else (node.closest('li') ?? node).setAttribute('hidden', '');
        }
      }
      if (node instanceof HTMLImageElement && /noe-logo/.test(node.getAttribute('src') ?? '')) {
        const dark = /noe-logo-dark/.test(node.getAttribute('src') ?? '');
        const src = (dark ? b.logoDarkUrl : null) ?? b.logoUrl;
        if (src) node.setAttribute('src', src);
        else if (!node.dataset.wl) {
          // No logo: the name in type, in its place.
          node.dataset.wl = '1';
          node.style.display = 'none';
          const name = document.createElement('span');
          name.textContent = b.displayName;
          name.className = 'font-semibold text-lg';
          node.after(name);
        }
      }
    }
    // The tab icon.
    document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]').forEach((l) => {
      const icon = b.faviconUrl ?? b.logoUrl;
      if (icon && l.href !== icon) l.href = icon;
    });
  };
}
